/**
 * Instrument Detail Hook
 * 
 * Fetches data for the instrument detail drawer
 * Handles both Asset scope (aggregate) and Position scope (single account)
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/services/LoggingService';
import type { UnifiedAsset } from './useAssetsUnified';

export type InstrumentScope = 'asset' | 'position';

interface UseInstrumentDetailParams {
  assetId: string | null;
  scope: InstrumentScope;
  accountId?: string; // Required for position scope
}

interface InstrumentDetailData {
  asset: UnifiedAsset;
  positions: Array<{
    id: string;
    account_id: string;
    account_name: string;
    institution_name: string;
    quantity: number;
    market_value: number;
    book_value?: number | null;
    as_of: string;
    provider_name: string;
    registered: boolean;
  }>;
  accountMetadata?: {
    account_name: string;
    account_type: string;
    institution_name: string;
    institution_logo_url?: string | null;
    institution_identifier?: string | null;
    registered: boolean;
    provider_name: string;
    last_synced: string;
  };
  valuationHistory: Array<{
    id: string;
    as_of: string;
    market_value: number;
    pricing_method: string;
    provider: string;
  }>;
}

export function useInstrumentDetail({ assetId, scope, accountId }: UseInstrumentDetailParams) {
  return useQuery({
    queryKey: ['instrument-detail', assetId, scope, accountId],
    queryFn: async (): Promise<InstrumentDetailData> => {
      if (!assetId) throw new Error('Asset ID required');

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Query assets_unified_view
      const { data: asset, error: assetError } = await supabase
        .from('assets_unified_view')
        .select('*')
        .eq('id', assetId)
        .eq('user_id', user.id)
        .single();

      if (assetError) throw assetError;
      if (!asset) throw new Error('Asset not found');

      // For manual assets, merge canonical fields from investment_assets (quantity, book_value, etc.)
      let mergedAsset = asset as UnifiedAsset;
      if ((asset as any)?.source_type === 'manual') {
        const { data: manualDetails } = await supabase
          .from('investment_assets')
          .select('quantity, book_value, description, registered, pricing_method, as_of, currency, market_value')
          .eq('user_id', user.id)
          .eq('id', assetId)
          .single();
        if (manualDetails) {
          const norm = { ...manualDetails, currency: String(manualDetails.currency || '').toUpperCase() };
          mergedAsset = { ...(asset as any), ...norm } as UnifiedAsset;
        }
      }

      // Fetch positions for this asset
      let positions: any[] = [];
      if ((asset as any).source_type === 'account') {
        try { logger.debug('useInstrumentDetail', 'Fetching positions by account UUID', { assetId, accountUuid: (asset as any).id }); } catch {}
        // Always try account UUID first (assets_unified_view.id === investment_accounts.id)
        const accountUuid = (asset as any).id as string; // account UUID
        const { data: byUuid } = await supabase
          .from('investment_holdings')
          .select(`id, account_id, account_uuid, asset_name, symbol, quantity, market_value, book_value, currency, asset_type, as_of, registered, pricing_method`)
          .eq('user_id', user.id)
          .eq('account_uuid', accountUuid);
        if (Array.isArray(byUuid) && byUuid.length > 0) {
          try { logger.debug('useInstrumentDetail', 'Found positions by account UUID', { count: byUuid.length }); } catch {}
          positions = byUuid;
        } else if ((asset as any).account_identifier) {
          // Fallback to account_id (account_number) if available
          const accountIdentifier = (asset as any).account_identifier as string;
          const { data: byNum } = await supabase
            .from('investment_holdings')
            .select(`id, account_id, account_uuid, asset_name, symbol, quantity, market_value, book_value, currency, asset_type, as_of, registered, pricing_method`)
            .eq('user_id', user.id)
            .eq('account_id', accountIdentifier);
          try { logger.debug('useInstrumentDetail', 'Positions by account_id fallback', { count: (byNum || []).length }); } catch {}
          positions = byNum || [];
        } else {
          // Final fallback: match holdings by account name -> account UUID
          try { logger.debug('useInstrumentDetail', 'No positions by UUID or account_id; trying account name fallback', { accountName: (asset as any).asset_name }); } catch {}
          const { data: acctByName } = await supabase
            .from('investment_accounts')
            .select('id')
            .eq('user_id', user.id)
            .eq('account_name', (asset as any).asset_name)
            .limit(1);
          const fallbackUuid = Array.isArray(acctByName) && acctByName.length > 0 ? (acctByName[0] as any).id : null;
          if (fallbackUuid) {
            const { data: byNameUuid } = await supabase
              .from('investment_holdings')
              .select(`id, account_id, account_uuid, asset_name, symbol, quantity, market_value, book_value, currency, asset_type, as_of, registered, pricing_method`)
              .eq('user_id', user.id)
              .eq('account_uuid', fallbackUuid);
            try { logger.debug('useInstrumentDetail', 'Positions by account name fallback', { count: (byNameUuid || []).length }); } catch {}
            positions = byNameUuid || [];
          } else {
            positions = [];
          }
        }
      } else {
        // Fallback: match by asset identity (name and optionally symbol)
        const symbol = (asset as any).symbol || null;
        const base = supabase
          .from('investment_holdings')
          .select(`id, account_id, account_uuid, asset_name, symbol, quantity, market_value, book_value, currency, asset_type, as_of, registered, pricing_method`)
          .eq('user_id', user.id)
          .eq('asset_name', (asset as any).asset_name);
        const { data: byName } = await base;
        if (Array.isArray(byName) && byName.length > 0) {
          positions = byName;
        }
        if ((!Array.isArray(byName) || byName.length === 0) && symbol) {
          const { data: bySymbol } = await supabase
            .from('investment_holdings')
            .select(`id, account_id, account_uuid, asset_name, symbol, quantity, market_value, book_value, currency, asset_type, as_of, registered, pricing_method`)
            .eq('user_id', user.id)
            .eq('symbol', symbol);
          positions = bySymbol || [];
        }
      }
      try { logger.debug('useInstrumentDetail', 'Transformed positions count', { count: (positions || []).length }); } catch {}

      const transformedPositions = (positions || []).map((p: any) => ({
        id: p.id,
        account_id: p.account_id,
        account_name: (p as any).investment_accounts?.account_name || (asset as any).asset_name || 'Unknown',
        institution_name: (p as any).investment_accounts?.investment_institutions?.institution_name || (asset as any).institution_name || 'Unknown',
        asset_type: p.asset_type || 'unclassified',
        asset_name: p.asset_name,
        symbol: p.symbol || null,
        quantity: p.quantity || 0,
        market_value: p.market_value || 0,
        book_value: (p.book_value === null || p.book_value === undefined) ? null : Number(p.book_value),
        currency: p.currency || (asset as any).currency || 'CAD',
        as_of: p.as_of || new Date().toISOString(),
        provider_name: asset.provider_name || 'manual',
        registered: p.registered || false,
        pricing_method: p.pricing_method || null,
      }));

      // Fetch account metadata for Position scope, and for Asset scope when the asset is an account
      let accountMetadata;
      if (scope === 'position' && accountId) {
        // Try to fetch from authoritative accounts table with institution join
        const { data: acctRows } = await (supabase
          .from('investment_accounts')
          .select('account_name, account_type, registered, updated_at, investment_institutions(institution_name, institution_identifier, logo_proxied_url, logo_url)') as any)
          .eq('user_id', user.id)
          .eq('account_number', accountId)
          .limit(1);

        const acct = Array.isArray(acctRows) && acctRows[0];
        if (acct) {
          accountMetadata = {
            account_name: (acct as any).account_name,
            account_type: (acct as any).account_type || 'Investment Account',
            institution_name: (acct as any).investment_institutions?.institution_name || 'Unknown Institution',
            institution_logo_url: (acct as any).investment_institutions?.logo_proxied_url || (acct as any).investment_institutions?.logo_url || null,
            institution_identifier: (acct as any).investment_institutions?.institution_identifier || null,
            registered: !!(acct as any).registered,
            provider_name: (asset as any).provider_name || 'connected',
            last_synced: (acct as any).updated_at || (asset as any).updated_at || new Date().toISOString(),
          };
        } else if (transformedPositions.length > 0) {
          // Fallback to data derived from positions
          const position = transformedPositions.find((p: any) => p.account_id === accountId) || transformedPositions[0];
          if (position) {
            accountMetadata = {
              account_name: position.account_name,
              account_type: 'Investment Account',
              institution_name: position.institution_name,
              institution_logo_url: null,
              institution_identifier: null,
              registered: position.registered,
              provider_name: position.provider_name,
              last_synced: (asset as any).updated_at || new Date().toISOString(),
            };
          }
        }
      }

      // Populate metadata in asset scope for accounts so Overview can show Institution row
      if (!accountMetadata && (asset as any).source_type === 'account') {
        const accountUuidForMeta = (asset as any).id as string;
        const { data: acctRowsForMeta } = await (supabase
          .from('investment_accounts')
          .select('account_name, account_type, registered, updated_at, investment_institutions(institution_name, institution_identifier, logo_proxied_url, logo_url)') as any)
          .eq('user_id', user.id)
          .eq('id', accountUuidForMeta)
          .limit(1);
        const acctForMeta = Array.isArray(acctRowsForMeta) && acctRowsForMeta[0];
        if (acctForMeta) {
          accountMetadata = {
            account_name: (acctForMeta as any).account_name,
            account_type: (acctForMeta as any).account_type || 'Investment Account',
            institution_name: (acctForMeta as any).investment_institutions?.institution_name || 'Unknown Institution',
            institution_logo_url: (acctForMeta as any).investment_institutions?.logo_proxied_url || (acctForMeta as any).investment_institutions?.logo_url || null,
            institution_identifier: (acctForMeta as any).investment_institutions?.institution_identifier || null,
            registered: !!(acctForMeta as any).registered,
            provider_name: (asset as any).provider_name || 'connected',
            last_synced: (acctForMeta as any).updated_at || (asset as any).updated_at || new Date().toISOString(),
          };
        }
      }

      // Fetch valuation history (table exists but not in types)
      // NOTE: 'provider' is not a column on valuation_history. Select only existing columns
      // and map provider from the parent asset for display purposes.
      const vhBuilder = supabase
        .from('valuation_history')
        .select('id, as_of, market_value, pricing_method, currency')
        .eq('user_id', user.id)
        .eq('source_id', assetId) as any;

      const { data: valuationHistory } = await vhBuilder
        .order('as_of', { ascending: false })
        .limit(50);

      // Maintain UI contract by injecting a provider field using the asset's provider_name
      const mappedValuationHistory = (valuationHistory || []).map((vh: any) => ({
        ...vh,
        provider: (asset as UnifiedAsset).provider_name || null,
      }));

      return {
        asset: mergedAsset as UnifiedAsset,
        positions: transformedPositions,
        accountMetadata,
        valuationHistory: mappedValuationHistory,
      };
    },
    enabled: !!assetId,
    staleTime: 2 * 60 * 1000,
  });
}
